import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useSalesKpis } from "@/hooks/useInvoices";
import { useGoals, useUpsertPersonalGoal, useUpsertTeamGoal } from "@/hooks/useGoals";
import { GoalEditDialog } from "@/components/goals/GoalEditDialog";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { formatKpiCurrency } from "@/lib/formats/currency";

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return formatKpiCurrency(value);
}

interface ForecastResult {
  onTrack: boolean;
  delta: number;
  label: string;
}

/**
 * Forecast for YTD travel sales.
 * invoicedYtd already captures all non-voided invoices (paid + open) for trips
 * returning this year, so the projection is a straight linear extrapolation.
 */
function computeForecast(
  invoicedYtd: number,
  goalAmount: number,
): ForecastResult {
  if (goalAmount <= 0) {
    return { onTrack: true, delta: 0, label: "" };
  }

  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear() + 1, 0, 1);
  const totalDays = (yearEnd.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24);
  const elapsedDays = Math.max(1, (now.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24));
  const fractionElapsed = elapsedDays / totalDays;

  const projected = invoicedYtd / fractionElapsed;
  const delta = projected - goalAmount;
  const onTrack = delta >= 0;

  const absDelta = Math.abs(delta);
  const label = onTrack
    ? `On pace to exceed sales goal by ${formatCompact(absDelta)}`
    : `Projected to finish ${formatCompact(absDelta)} below goal`;

  return { onTrack, delta, label };
}

interface TraveledSalesCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function TraveledSalesCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: TraveledSalesCardProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
  } = useSalesKpis({ tramsAccountId, viewMode, returnDateFrom, returnDateTo });

  const {
    data: goalsData,
    isLoading: goalsLoading,
    error: goalsError,
  } = useGoals(currentYear);

  const upsertPersonal = useUpsertPersonalGoal();
  const upsertTeam = useUpsertTeamGoal();

  const isLoading = kpiLoading || goalsLoading;
  const error = kpiError || goalsError;

  const hasTeamAccess = goalsData?.showTeamByDefault ?? false;
  const canEditTeamGoals = goalsData?.canEditTeamGoals ?? false;

  const goalScope: "team" | "personal" =
    viewMode === "all" && (hasTeamAccess || canEditTeamGoals)
      ? "team"
      : "personal";

  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const goalAmount = parseFloat(activeGoal?.salesGoal ?? "0");
  const invoicedAmount = parseFloat(kpiData?.invoicedYtd ?? "0");

  const forecast = useMemo(
    () => computeForecast(invoicedAmount, goalAmount),
    [invoicedAmount, goalAmount],
  );

  const canEdit =
    goalScope === "personal" ||
    (goalScope === "team" && canEditTeamGoals);

  if (error) {
    return (
      <SectionLoadError
        sectionName="YTD Projected Sales"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/sales-kpis"] });
          queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5">
        <Skeleton className="h-4 w-36 mb-3" />
        <Skeleton className="h-10 w-44 mb-1" />
        <Skeleton className="h-3 w-28 mb-3" />
        <Skeleton className="h-6 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            YTD Projected Sales
          </p>
          <InfoHint label="How YTD projected sales is calculated">
            <p>
              Year-to-date sales from non-voided invoices, by trip return date.
            </p>
            <KpiBasisNote card="ytdProjectedSales" />
          </InfoHint>
        </div>
        <KpiBasisLabel card="ytdProjectedSales" className="mb-1" />
        <p className="text-4xl font-bold tracking-tight text-foreground">
          {formatKpiCurrency(invoicedAmount)}
        </p>

        {goalAmount > 0 && (
          <p className="text-lg text-muted-foreground mt-0.5">
            /{formatKpiCurrency(goalAmount)}
          </p>
        )}
      </div>

      {goalAmount > 0 ? (
        <div className="bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-2 mt-auto border-t border-border/50">
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold ${
                forecast.onTrack
                  ? "text-green-700 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {forecast.onTrack ? "On Track" : "Off Track"}
            </p>
            <p className="text-sm text-muted-foreground break-words">
              {forecast.label}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Includes closed and open bookings returning this year.
            </p>
          </div>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      ) : (
        <div className="bg-muted/40 px-5 py-3.5 flex items-center justify-between gap-2 mt-auto border-t border-border/50">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">
              No sales goal set
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Includes closed and open bookings returning this year.
            </p>
          </div>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      )}
    </div>
  );
}
